import { getAppVersion, getSecondaryTokenBridgeFunction } from './sessionStorage'

// Flutter InAppWebView type declarations
declare global {
  interface Window {
    flutter_inappwebview?: {
      callHandler: (handlerName: string, ...args: any[]) => Promise<any>
    }
    setSecondaryToken?: (secondaryAuthHeaderKey: string, secondaryAuthHeaderValue: string) => void
    setAllSecondaryTokens?: (tokens: string | Record<string, string>) => void
  }
}

/**
 * Simple function to send message string to Flutter app via InAppWebView
 * Similar to how uniAppHandle.postMessage works in appBridge.ts
 *
 * @param messageString - Complete message string to send
 */
const sendToFlutter = (messageString: string): void => {
  try {
    if (window?.flutter_inappwebview && typeof window?.flutter_inappwebview?.callHandler === 'function') {
      window?.flutter_inappwebview
        ?.callHandler('puHandle', messageString)
        .then(function (result) {
          console.log('Result from Flutter: ', result)
        })
        .catch(function (error) {
          console.error('Flutter InAppWebView callHandler error:', error)
        })
    } else {
      console.error('Flutter InAppWebView is not available yet.')
    }
  } catch (error: any) {
    console.error('Failed to send to Flutter:', error.message)
  }
}

export const trackEvent = ({
  eventName,
  payload
}: {
  eventName: string
  payload: {
    ScreenName?: string
    screenType?: string
    responseName?: string
    fundName?: string
    fundIsin?: string
    eventId?: string
    buttonLabel?: string
    scrollSection?: string
  }
}): void => {
  try {
    // Build string exactly like appBridge.ts trackEvent
    const messageString = `{
					"function": "event",
          "eventName" : "${eventName}",
					"payload" : ${JSON.stringify(payload)}
				}`
    sendToFlutter(messageString)
  } catch (e: any) {
    console.error('Error occurred while tracking event:', e.message)
  }
}

export const sendCTA = (ctaData: string): void => {
  try {
    // Build string exactly like appBridge.ts postCTA
    const messageString = `{"function": "cta", "extras": ${ctaData}}`
    sendToFlutter(messageString)
  } catch (e: any) {
    console.error('Failed in send CTA:', e.message)
  }
}

export const sendbase64ImageUpload = (base64ImageCtaString: string): void => {
  try {
    const messageString = `{"function": "base64ImageUpload", "extras": ${base64ImageCtaString}}`
    sendToFlutter(messageString)
  } catch (e: any) {
    console.error('Failed in send base64 image upload:', e.message)
  }
}

export const handleClose = (): void => {
  try {
    sendToFlutter(`{"function":"cta",  "extras":{"type":"CTA","navigationStyle":"POP"}}`)
  } catch (e: any) {
    console.error('Failed in handle close:', e.message)
  }
}

export const goToPortfolioDashboard = (): void => {
  try {
    sendToFlutter(
      `{"function":"cta","extras":{"type":"CTA","ctaType":"DEFAULT","method":"GET","navigationStyle":"PUSH_AND_REMOVE_ALL","url":"/v1/powerup/user/home?tabTypeEnum=portfolio"}}`
    )
  } catch (e: any) {
    console.error('Failed to redirect to portfolio dashboard:', e.message)
  }
}

export const requestSecondaryTokenFromFlutter = (): void => {
  try {
    sendToFlutter(`{"function":"${getSecondaryTokenBridgeFunction()}"}`)
  } catch (e: any) {
    console.error('Failed to request secondary token from Flutter:', e.message)
  }
}

export const requestAppDataFromFlutter = (): void => {
  try {
    sendToFlutter('{"function":"token"}')
    sendToFlutter('{"function":"secondaryToken"}')
    sendToFlutter('{"function":"metaData"}')
    sendToFlutter('{"function":"passSafeAreaPadding"}')
    // If app-version is already known (e.g. remount), request immediately.
    // Otherwise setUniMetaData will request after metadata arrives.
    if (getAppVersion()) {
      requestSecondaryTokenFromFlutter()
    }
  } catch (e: any) {
    console.error('Failed to request app data from Flutter:', e.message)
  }
}

export const generateMFCAgainCTA = (): void => {
  try {
    const messageString =
      '{"function":"cta","extras":{"type":"CTA","ctaType":"DEFAULT","method":"POST","navigationStyle":"PUSH_REPLACEMENT","url":"/v2/portfolio/initiate-txn"}}'
    sendToFlutter(messageString)
  } catch (e: any) {
    console.error('error generating mfc again cta', e.message)
  }
}
